import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { notification } from "top-component-library";
import { downloadDataFromBrowser, generalErrorAlert } from "util/Utils";
import {
  IntegrationCustomerOption,
  MasterSyncSettings,
  OmnivoreLocationOptions,
  OmnivoreSettings,
  SelectedCustomer,
} from "./types";

// Backs the "Sync from Master Account" / "POS Integrations" management remnant
// (addIntegrationController.js). Fetches are lazy, per dialog open, matching legacy's own
// $scope.init() - which only ever loads the one integration type its dialog was opened for,
// not all 4 upfront. Only ever manages an *already-connected* Omnivore integration; new
// Omnivore connections happen through the separate App Store (OmnivoreIntegrationModal).
export const useIntegrationsRemnant = (selectedCustomer?: SelectedCustomer) => {
  const customerId = selectedCustomer?.customer_id;

  const [allowedCustomers, setAllowedCustomers] = useState<IntegrationCustomerOption[]>([]);
  const [masterSync, setMasterSync] = useState<MasterSyncSettings | null>(null);
  const [omnivoreMms, setOmnivoreMms] = useState<OmnivoreSettings | null>(null);
  const [omnivoreDirect, setOmnivoreDirect] = useState<OmnivoreSettings | null>(null);
  const [omnivore3, setOmnivore3] = useState<OmnivoreSettings | null>(null);
  const [locationOptions, setLocationOptions] = useState<OmnivoreLocationOptions | null>(null);
  const [isLoadingLocationOptions, setIsLoadingLocationOptions] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState<{ status: string; outputFileUrl?: string } | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Mirrors getConnectionStatusForIntegration().
  const loadStatus = useCallback(
    async <T>(displayName: string, url: string, setState: (data: T) => void) => {
      if (!customerId) return;
      try {
        const res = await axios.get<T>(url, { params: { customer_id: customerId } });
        setState(res.data);
      } catch (error) {
        generalErrorAlert(error, `Trouble retrieving ${displayName} settings, please try again or contact Bbot.`);
      }
    },
    [customerId]
  );

  const loadMasterSync = useCallback(async () => {
    if (!customerId) return;
    try {
      const [customersRes] = await Promise.all([
        axios.get<{ customers: IntegrationCustomerOption[] }>("/owner/getAllowedCustomers"),
        loadStatus<MasterSyncSettings>("MasterSync", "/admin/getMasterAccountIntegrationStatus", setMasterSync),
      ]);
      setAllowedCustomers(customersRes.data.customers.filter((customer) => customer.customer_id !== customerId));
    } catch (error) {
      generalErrorAlert(
        error,
        "Trouble retrieving master account integration settings, please try again or contact Bbot."
      );
    }
  }, [customerId, loadStatus]);

  const loadOmnivoreMms = useCallback(
    () => loadStatus<OmnivoreSettings>("Omnivore MMS", "/admin/getOmnivoreConnectionStatus", setOmnivoreMms),
    [loadStatus]
  );

  // Mirrors getOmnivoreDirectLocationOptions()/getOmnivore3LocationOptions(): re-fetched
  // whenever the location id field changes, shared by both Direct and Omnivore 3's forms.
  const loadLocationOptions = useCallback(async (locationId: string) => {
    if (!locationId) {
      setLocationOptions(null);
      return;
    }
    setIsLoadingLocationOptions(true);
    try {
      const res = await axios.get<OmnivoreLocationOptions>("/admin/getOmnivoreDirectLocationOptions", {
        params: { location_id: locationId },
      });
      setLocationOptions(res.data);
    } catch (error) {
      generalErrorAlert(error, "Unable to connect to specified location ID.");
      setLocationOptions(null);
    } finally {
      setIsLoadingLocationOptions(false);
    }
  }, []);

  const loadOmnivoreDirect = useCallback(async () => {
    await loadStatus<OmnivoreSettings>(
      "Omnivore Direct",
      "/admin/getOmnivoreDirectConnectionStatus",
      setOmnivoreDirect
    );
  }, [loadStatus]);

  const loadOmnivore3 = useCallback(async () => {
    await loadStatus<OmnivoreSettings>("Omnivore 3", "/admin/getOmnivore3ConnectionStatus", setOmnivore3);
  }, [loadStatus]);

  // Mirrors updateMasterAccountIntegration(): sends the whole fetched-then-edited settings
  // object back, not a diff.
  const saveMasterSync = useCallback(
    async (settings: MasterSyncSettings) => {
      if (!customerId) return false;
      try {
        await axios.post("/admin/updateMasterAccountIntegration", { ...settings, customer_id: customerId });
        notification.success({ message: "Master sync properties updated." });
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble updating master sync properties.");
        return false;
      }
    },
    [customerId]
  );

  // Mirrors connectIntegration(): shared POST/success/error handling for all 3 Omnivore
  // variants' connect calls.
  const connectOmnivoreVariant = useCallback(
    async (displayName: string, url: string, settings: OmnivoreSettings) => {
      if (!customerId) return false;
      try {
        await axios.post(url, { ...settings, customer_id: customerId });
        notification.success({ message: `${displayName} settings saved.` });
        return true;
      } catch (error) {
        generalErrorAlert(error, `Error saving ${displayName} settings, please try again or contact Bbot.`);
        return false;
      }
    },
    [customerId]
  );

  const connectOmnivoreMms = useCallback(
    (settings: OmnivoreSettings) => connectOmnivoreVariant("Omnivore MMS", "/admin/connectOmnivore", settings),
    [connectOmnivoreVariant]
  );

  const connectOmnivoreDirect = useCallback(
    (settings: OmnivoreSettings) => connectOmnivoreVariant("Omnivore Direct", "/admin/connectOmnivoreDirect", settings),
    [connectOmnivoreVariant]
  );

  // Mirrors connectOmnivore3(): available_order_types is always overwritten with the full
  // order_types list just fetched for this location, never user-edited.
  const connectOmnivore3 = useCallback(
    (settings: OmnivoreSettings) =>
      connectOmnivoreVariant("Omnivore", "/admin/connectOmnivore3", {
        ...settings,
        available_order_types: locationOptions?.order_types ?? [],
      }),
    [connectOmnivoreVariant, locationOptions]
  );

  // Mirrors disconnectIntegration(): shared across all 3 Omnivore variants, admin-only.
  const disconnectOmnivoreVariant = useCallback(
    async (displayName: string, url: string, settings: OmnivoreSettings, deleteMenuItems: boolean) => {
      if (!customerId) return false;
      try {
        await axios.post(url, { ...settings, customer_id: customerId, delete_menu_items: deleteMenuItems });
        notification.success({ message: `${displayName} disconnected.` });
        return true;
      } catch (error) {
        generalErrorAlert(error, `Trouble disconnecting ${displayName}, please try again or contact Bbot.`);
        return false;
      }
    },
    [customerId]
  );

  const disconnectOmnivoreMms = useCallback(
    (deleteMenuItems: boolean) =>
      omnivoreMms &&
      disconnectOmnivoreVariant("Omnivore MMS", "/admin/disconnectOmnivore", omnivoreMms, deleteMenuItems),
    [disconnectOmnivoreVariant, omnivoreMms]
  );

  const disconnectOmnivoreDirect = useCallback(
    (deleteMenuItems: boolean) =>
      omnivoreDirect &&
      disconnectOmnivoreVariant("Omnivore Direct", "/admin/disconnectOmnivoreDirect", omnivoreDirect, deleteMenuItems),
    [disconnectOmnivoreVariant, omnivoreDirect]
  );

  const disconnectOmnivore3 = useCallback(
    (deleteMenuItems: boolean) =>
      omnivore3 && disconnectOmnivoreVariant("Omnivore 3", "/admin/disconnectOmnivore3", omnivore3, deleteMenuItems),
    [disconnectOmnivoreVariant, omnivore3]
  );

  // Stops an in-flight diagnostic poll. Exposed so a dialog can cancel it on close - it
  // otherwise has no owner once the initiating dialog goes away, since this hook (and its
  // polling) outlives any single dialog open/close cycle.
  const stopDiagnostic = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Belt-and-suspenders for the same leak stopDiagnostic guards against, for whenever the
  // whole page (not just a dialog) goes away mid-poll.
  useEffect(() => stopDiagnostic, [stopDiagnostic]);

  // Mirrors checkOmnivoreMenus()/checkOmnivoreDirect()/_pollForOmnivoreDebugLog(): kicks off
  // a debug run, then polls every second until it completes or fails. Admin-only, MMS/Direct
  // only - Omnivore 3 has no diagnostic UI in legacy either. Unlike legacy's own polling
  // (which never catches a failed poll request either), a poll failure here stops retrying
  // and surfaces an error instead of retrying forever in silence.
  const runDiagnostic = useCallback(
    async (debugUrl: string, pollUrl: string) => {
      if (!customerId) return;
      setDiagnosticResult(null);
      try {
        const res = await axios.post<{ results: string }>(debugUrl, { customer_id: customerId });
        const resultKey = res.data.results;
        stopDiagnostic();
        pollIntervalRef.current = setInterval(async () => {
          try {
            const pollRes = await axios.get<{ status: string; output_file_url?: string }>(pollUrl, {
              params: { result_key: resultKey },
            });
            if (pollRes.data.status === "complete" || pollRes.data.status === "failed") {
              stopDiagnostic();
              setDiagnosticResult({ status: pollRes.data.status, outputFileUrl: pollRes.data.output_file_url });
            }
          } catch (error) {
            stopDiagnostic();
            generalErrorAlert(error, "Error checking diagnostic. Please try again or contact Bbot.");
          }
        }, 1000);
      } catch (error) {
        generalErrorAlert(error, "Error checking diagnostic. Please try again or contact Bbot.");
      }
    },
    [customerId, stopDiagnostic]
  );

  const checkOmnivoreMmsDiagnostic = useCallback(
    () => runDiagnostic("/admin/debugOmnivore", "/admin/getDebugOmnivoreResults"),
    [runDiagnostic]
  );

  const checkOmnivoreDirectDiagnostic = useCallback(
    () => runDiagnostic("/admin/debugOmnivoreDirect", "/admin/getDebugOmnivoreDirectResults"),
    [runDiagnostic]
  );

  // Mirrors getOmnivorePOSItems()/getOmnivoreDirectPOSItems(): downloads a JSON dump for
  // debugging. Admin-only, MMS/Direct only.
  const downloadPosItems = useCallback(
    async (url: string, fileNamePrefix: string) => {
      if (!customerId) return;
      try {
        const res = await axios.get(url, { params: { customer_id: customerId } });
        downloadDataFromBrowser(JSON.stringify(res.data), `${fileNamePrefix}_${customerId}`, "txt");
      } catch (error) {
        generalErrorAlert(error, "Error retrieving POS items.");
      }
    },
    [customerId]
  );

  const downloadOmnivoreMmsPosItems = useCallback(
    () => downloadPosItems("/admin/getOmnivorePOSItems", "omnivore_diagnostic"),
    [downloadPosItems]
  );

  const downloadOmnivoreDirectPosItems = useCallback(
    () => downloadPosItems("/admin/getOmnivoreDirectPOSItems", "omnivore_direct_diagnostic"),
    [downloadPosItems]
  );

  return {
    allowedCustomers,
    masterSync,
    loadMasterSync,
    saveMasterSync,
    omnivoreMms,
    loadOmnivoreMms,
    connectOmnivoreMms,
    disconnectOmnivoreMms,
    checkOmnivoreMmsDiagnostic,
    downloadOmnivoreMmsPosItems,
    omnivoreDirect,
    loadOmnivoreDirect,
    connectOmnivoreDirect,
    disconnectOmnivoreDirect,
    checkOmnivoreDirectDiagnostic,
    downloadOmnivoreDirectPosItems,
    omnivore3,
    loadOmnivore3,
    connectOmnivore3,
    disconnectOmnivore3,
    locationOptions,
    isLoadingLocationOptions,
    loadLocationOptions,
    diagnosticResult,
    stopDiagnostic,
  };
};
